<template>
  <div
    class="participant-card"
    :class="[
      `participant-card--${variant}`,
      { 'participant-card--interactive': interactive },
    ]"
    @click="onClick"
    @mousedown="onMouseDown"
    @contextmenu="onContextMenu"
  >
    <div class="card-role" v-if="role">{{ role }}</div>
    <div class="card-fields">
      <div v-for="field in fields" :key="field.label" class="field-item">
        <span class="field-label">
          <BaseInfoTooltip
            v-if="fieldInfo(field)"
            :text="fieldInfo(field)"
            :filled="fieldFilled(field)"
            variant="text"
          >
            {{ field.label }}
          </BaseInfoTooltip>
          <span v-else>{{ field.label }}</span>
        </span>
        <span
          class="field-value"
          :class="{ 'field-value--with-action': field.action }"
        >
          <span class="field-value__content">
            <component
              v-if="field.component"
              :is="field.component"
              v-bind="field.componentProps || {}"
            />
            <template v-else>
              {{ field.value }}
            </template>
          </span>
          <component
            v-if="field.action"
            :is="field.action.component"
            v-bind="field.action.componentProps || {}"
          />
        </span>
      </div>
    </div>
    <BaseContextMenu
      v-if="interactive"
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :items="contextMenu.items"
      @close="closeContextMenu"
    />
  </div>
</template>

<script>
import { fieldInfoByKey } from "@/config/fieldInfo";

const OPEN_IN_NEW_TAB_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
  <polyline points="15 3 21 3 21 9"/>
  <line x1="10" y1="14" x2="21" y2="3"/>
</svg>`;

export default {
  name: "BaseParticipantCard",
  props: {
    role: {
      type: String,
      default: null,
    },
    routeName: {
      type: String,
      default: "",
    },
    entityId: {
      type: [String, Number],
      default: null,
    },
    fields: {
      type: Array,
      required: true,
    },
    interactive: {
      type: Boolean,
      default: true,
    },
    showFieldInfo: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "outlined"].includes(value),
    },
  },
  data() {
    return {
      contextMenu: { visible: false, x: 0, y: 0, items: [] },
    };
  },
  methods: {
    fieldFilled(field) {
      return field.value !== null && field.value !== undefined && field.value !== "";
    },

    fieldInfo(field) {
      if (!this.showFieldInfo) return "";
      return field.info || fieldInfoByKey(field.infoKey);
    },

    onClick(event) {
      if (!this.interactive) return;

      if (event.ctrlKey || event.metaKey) {
        this.openInNewTab();
        return;
      }
      this.goToDetails();
    },

    onMouseDown(event) {
      if (!this.interactive) return;

      if (event.button === 1) {
        event.preventDefault();
        this.openInNewTab();
      }
    },

    // TODO: вынести общую механику context menu в helper/composable.
    onContextMenu(event) {
      if (!this.interactive) return;

      event.preventDefault();
      let x = event.clientX;
      let y = event.clientY;
      if (x + 220 > window.innerWidth) x = window.innerWidth - 228;
      if (y + 44 > window.innerHeight) y = window.innerHeight - 52;

      this.contextMenu = {
        visible: true,
        x,
        y,
        items: [
          {
            key: "open-new-tab",
            label: "Открыть в новой вкладке",
            icon: OPEN_IN_NEW_TAB_ICON,
            action: () => this.openInNewTab(),
          },
        ],
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },

    goToDetails() {
      if (!this.routeName || this.entityId === null) return;
      this.$router.push({ name: this.routeName, params: { id: this.entityId } });
    },

    openInNewTab() {
      if (!this.routeName || this.entityId === null) return;
      const route = this.$router.resolve({ name: this.routeName, params: { id: this.entityId } });
      window.open(route.href, "_blank");
    },

    onKeyDown(event) {
      if (event.key === "Escape") this.closeContextMenu();
    },
  },
  mounted() {
    document.addEventListener("click", this.closeContextMenu);
    document.addEventListener("keydown", this.onKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeContextMenu);
    document.removeEventListener("keydown", this.onKeyDown);
  },
};
</script>

<style scoped>
.participant-card {
  border-radius: 8px;
  padding: 12px;
  background: rgb(240, 240, 240);
  border: 1px solid #e8e8e8;
}

.participant-card--outlined {
  padding: 12px 14px;
  border-color: #e3e6ee;
  border-radius: 10px;
  background: #fff;
}

.participant-card--interactive {
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease,
    box-shadow 0.3s ease;
}

.participant-card--interactive:hover {
  background: #e6f5ed;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-role {
  display: inline-block;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.participant-card--outlined .card-role {
  margin-bottom: 12px;
  padding: 3px 8px;
  border-radius: 4px;
  background: #e6f8f5;
  color: #047a69;
  font-size: 12px;
}

.card-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.field-value {
  font-weight: 500;
}

.field-value--with-action {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.field-value__content {
  min-width: 0;
}

.participant-card--outlined .field-value {
  font-size: 14px;
}

.participant-card--outlined .field-item {
  flex-direction: column;
  justify-content: initial;
}

@media (min-width: 1600px) {
  .field-item {
    flex-direction: row;
    justify-content: space-between;
  }
}
</style>
